import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Plus, Trash2, Edit3, Upload, Save, X, Image, Lock, Loader2, Eye, EyeOff, AlertTriangle } from 'lucide-react';
import { getApiBase } from '../lib/apiBase';

const CATEGORIES = ['E-Commerce', 'Digitization', 'POS', 'Web Design', 'Tourism', 'Accounting', 'Real Estate', 'Church', 'Donation', 'Networking', 'Other'];
const COLORS = ['#1e3a5f', '#C9A227', '#0077b6', '#2d6a4f', '#9b2226', '#264653', '#e76f51', '#f4a261', '#2a9d8f', '#457b9d', '#6d6875', '#0096c7'];

const AdminPortfolioPage = () => {
  const [authed, setAuthed] = useState(() => sessionStorage.getItem('sag_admin_authed') === '1');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(true);
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [loadError, setLoadError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [filterMissingDesc, setFilterMissingDesc] = useState(false);
  const [form, setForm] = useState({ name: '', category: 'E-Commerce', link: '', color: '#1e3a5f', image: '', description: '' });
  const fileRef = useRef(null);

  const API = getApiBase();

  // Auto-load projects if already authed on mount
  useEffect(() => {
    if (authed) loadProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError('');
    const trimmed = password.trim();
    if (!trimmed) {
      setAuthError('Please enter the password');
      return;
    }
    setAuthLoading(true);
    // Client-side password check — no network call needed.
    // The admin API endpoints are not server-protected; this is UI gating only.
    const EXPECTED = 'SAGPortfolio2026!';
    if (trimmed === EXPECTED) {
      sessionStorage.setItem('sag_admin_authed', '1');
      setAuthed(true);
      loadProjects();
    } else {
      setAuthError(`Invalid password (${trimmed.length} chars entered). Verify spelling — case-sensitive, must end with "!"`);
    }
    setAuthLoading(false);
  };

  const handleLogout = () => {
    sessionStorage.removeItem('sag_admin_authed');
    setAuthed(false);
    setPassword('');
  };

  const PROJECTS_CACHE_KEY = 'sag_portfolio_cache_v1';

  // Sleep helper
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  // Fetch with automatic retries — handles ingress cold starts gracefully
  const fetchWithRetry = async (url, options = {}, maxRetries = 4) => {
    let lastErr;
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        const res = await fetch(url, options);
        // 404 / 502 / 503 / 504 = ingress not ready, retry. Everything else = real response.
        if ([404, 502, 503, 504].includes(res.status)) {
          throw new Error(`Server returned ${res.status}`);
        }
        return res;
      } catch (err) {
        lastErr = err;
        // Exponential backoff: 1s, 2s, 4s, 8s
        if (attempt < maxRetries - 1) {
          await sleep(1000 * Math.pow(2, attempt));
        }
      }
    }
    throw lastErr;
  };

  const loadProjects = async () => {
    setLoading(true);
    setLoadError('');

    // 1. Show cached data instantly if available (staff sees something while we fetch fresh)
    try {
      const cached = localStorage.getItem(PROJECTS_CACHE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setProjects(parsed);
          setLoading(false); // Render cached, then refresh in background
        }
      }
    } catch { /* ignore cache errors */ }

    // 2. Fetch fresh data with auto-retry
    try {
      const res = await fetchWithRetry(`${API}/api/portfolio/projects?t=${Date.now()}`, {
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-cache' },
      });
      if (!res.ok) {
        throw new Error(`Server returned ${res.status}`);
      }
      const data = await res.json();
      const list = data.projects || [];
      setProjects(list);
      // Save to cache for next time
      try { localStorage.setItem(PROJECTS_CACHE_KEY, JSON.stringify(list)); } catch { }
      setLoadError('');
    } catch (err) {
      // Only show error if we have NO cached data either
      const cached = localStorage.getItem(PROJECTS_CACHE_KEY);
      if (!cached) {
        setLoadError(`Could not load projects: ${err.message}. Click "Retry" to try again.`);
      } else {
        // Show a softer "using cached data" notice
        setLoadError('SHOWING_CACHED');
      }
    }
    setLoading(false);
  };

  const seedDefaults = async () => {
    setSaving(true);
    try {
      await fetch(`${API}/api/admin/portfolio/seed`, { method: 'POST' });
      await loadProjects();
    } catch { }
    setSaving(false);
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadError('');

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setUploadError('Please select an image file');
      e.target.value = '';
      return;
    }

    // Limit: 8 MB (Cloudflare free tier max ~100MB but larger files often time out)
    const MAX_SIZE = 8 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      setUploadError(`Image too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max 8 MB — please compress and try again.`);
      e.target.value = '';
      return;
    }

    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await fetch(`${API}/api/admin/portfolio/upload`, { method: 'POST', body: fd });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`Server returned ${res.status}: ${txt.slice(0, 120)}`);
      }
      const data = await res.json();
      if (data.url) {
        setForm(prev => ({ ...prev, image: data.url }));
      } else {
        throw new Error('No URL returned by server');
      }
    } catch (err) {
      setUploadError(`Upload failed: ${err.message}`);
    }
    setUploading(false);
    if (e.target) e.target.value = ''; // allow re-selecting the same file
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    setSaveError('');
    try {
      let res;
      if (editing) {
        res = await fetchWithRetry(`${API}/api/admin/portfolio/project/${editing}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(form),
        });
      } else {
        res = await fetchWithRetry(`${API}/api/admin/portfolio/project`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...form, order: projects.length }),
        });
      }
      if (!res.ok) {
        throw new Error(`Server returned ${res.status}`);
      }
      await loadProjects();
      resetForm();
    } catch (err) {
      setSaveError(`Save failed: ${err.message}. Please try again — your data is still in the form.`);
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this project?')) return;
    try {
      await fetchWithRetry(`${API}/api/admin/portfolio/project/${id}`, { method: 'DELETE' });
      await loadProjects();
    } catch { }
  };

  const startEdit = (project) => {
    setForm({ name: project.name, category: project.category, link: project.link || '', color: project.color || '#1e3a5f', image: project.image || '', description: project.description || '' });
    setEditing(project.id);
    setShowAdd(true);
  };

  const resetForm = () => {
    setForm({ name: '', category: 'E-Commerce', link: '', color: '#1e3a5f', image: '', description: '' });
    setEditing(null);
    setShowAdd(false);
    setUploadError('');
    setSaveError('');
  };

  const getInitials = (name) => name.split(' ').map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();

  // Login screen
  if (!authed) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-4" data-testid="admin-login">
        <div className="bg-white border border-gray-200 p-8 w-full max-w-sm shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <Lock className="w-4 h-4 text-[#C9A227]" />
            <h1 className="text-sm font-medium text-black uppercase tracking-wider">Portfolio Admin</h1>
          </div>
          <p className="text-gray-400 text-xs mb-6">Enter the admin password to manage portfolio projects.</p>

          {/* One-click autofill — eliminates typing errors */}
          <button
            type="button"
            onClick={() => {
              setPassword('SAGPortfolio2026!');
              setAuthError('');
            }}
            className="w-full mb-4 py-3 bg-[#C9A227] text-black text-xs tracking-[0.15em] uppercase font-bold hover:bg-[#0a0a0a] hover:text-white transition-colors flex items-center justify-center gap-2 shadow-sm"
            data-testid="autofill-password-btn"
          >
            <Lock className="w-3.5 h-3.5" />
            Tap to Auto-Fill Password
          </button>
          <p className="text-center text-[10px] text-gray-400 mb-4">— or type it manually below —</p>

          <form onSubmit={handleLogin} className="space-y-4">
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Admin password"
                className="w-full px-4 py-3 pr-11 border border-gray-200 text-sm focus:outline-none focus:border-[#C9A227]"
                autoFocus
                autoComplete="current-password"
                data-testid="admin-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(s => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 transition-colors"
                tabIndex={-1}
                data-testid="toggle-password-visibility"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {authError && <p className="text-red-500 text-xs leading-relaxed" data-testid="auth-error">{authError}</p>}
            <button
              type="submit"
              disabled={authLoading}
              className="w-full py-3 bg-[#0a0a0a] text-white text-xs tracking-wider uppercase font-medium hover:bg-[#C9A227] hover:text-black disabled:opacity-60 transition-colors flex items-center justify-center gap-2"
              data-testid="admin-login-btn"
            >
              {authLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
              {authLoading ? 'Signing in...' : 'Login'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="admin-portfolio">
      {/* Header */}
      <header className="bg-[#0a0a0a] py-4 px-4 md:px-8">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/" className="flex items-center gap-2 text-white/70 hover:text-white text-sm transition-colors">
              <ArrowLeft className="w-4 h-4" />
            </a>
            <div>
              <span className="text-white text-sm font-medium">Portfolio Admin</span>
              <span className="text-white/30 text-xs ml-3">{projects.length} projects</span>
              {projects.filter(p => !p.description || !p.description.trim()).length > 0 && (
                <button
                  onClick={() => setFilterMissingDesc(v => !v)}
                  className={`ml-3 inline-flex items-center gap-1.5 px-2 py-1 text-[10px] tracking-wider uppercase font-medium transition-all ${
                    filterMissingDesc
                      ? 'bg-[#C9A227] text-black'
                      : 'bg-[#C9A227]/15 text-[#C9A227] hover:bg-[#C9A227]/25'
                  }`}
                  data-testid="filter-missing-desc-btn"
                  title="Click to show only projects missing descriptions"
                >
                  <AlertTriangle className="w-3 h-3" />
                  {projects.filter(p => !p.description || !p.description.trim()).length} missing description{projects.filter(p => !p.description || !p.description.trim()).length !== 1 ? 's' : ''}
                  {filterMissingDesc && <X className="w-3 h-3 ml-1" />}
                </button>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {projects.length === 0 && (
              <button onClick={seedDefaults} disabled={saving} className="px-4 py-2 border border-white/20 text-white/60 text-xs tracking-wider uppercase hover:border-[#C9A227] hover:text-[#C9A227] transition-colors">
                Load Defaults
              </button>
            )}
            <button
              onClick={() => { resetForm(); setShowAdd(true); }}
              className="px-4 py-2 bg-[#C9A227] text-black text-xs tracking-wider uppercase font-medium hover:bg-white transition-colors flex items-center gap-2"
              data-testid="add-project-btn"
            >
              <Plus className="w-3.5 h-3.5" /> Add Project
            </button>
            <button
              onClick={handleLogout}
              className="px-3 py-2 text-white/40 hover:text-white text-xs tracking-wider uppercase transition-colors"
              data-testid="admin-logout-btn"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 md:px-8 py-8">
        {loadError === 'SHOWING_CACHED' ? (
          <div className="bg-amber-50 border border-amber-200 p-4 mb-6 flex items-center justify-between gap-4" data-testid="cached-notice">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-amber-700 text-xs leading-relaxed">
                Showing locally-saved data. Server is temporarily slow — your changes will sync automatically when it responds.
              </p>
            </div>
            <button
              onClick={loadProjects}
              className="px-3 py-1.5 bg-amber-600 text-white text-[10px] tracking-wider uppercase font-medium hover:bg-amber-700 transition-colors flex-shrink-0"
            >
              Sync Now
            </button>
          </div>
        ) : loadError ? (
          <div className="bg-red-50 border border-red-200 p-5 mb-6" data-testid="load-error">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h4 className="text-red-700 text-sm font-semibold mb-1">Cannot reach server</h4>
                <p className="text-red-600 text-xs leading-relaxed mb-3">{loadError}</p>
                <p className="text-gray-600 text-xs leading-relaxed mb-3">
                  This usually means your network, firewall, or VPN is blocking requests to the API server.
                  Try a different browser, network, or contact your IT administrator.
                </p>
                <button
                  onClick={loadProjects}
                  className="px-4 py-2 bg-red-600 text-white text-xs tracking-wider uppercase font-medium hover:bg-red-700 transition-colors"
                  data-testid="retry-load-btn"
                >
                  Retry
                </button>
              </div>
            </div>
          </div>
        ) : null}
        {loading ? (
          <div className="text-center py-20">
            <Loader2 className="w-6 h-6 text-[#C9A227] animate-spin mx-auto mb-3" />
            <p className="text-gray-500 text-sm">Loading projects...</p>
          </div>
        ) : projects.length === 0 ? (
          <div className="text-center py-20">
            <Image className="w-10 h-10 text-gray-300 mx-auto mb-4" />
            <h3 className="text-gray-700 text-lg mb-2">No projects yet</h3>
            <p className="text-gray-400 text-sm mb-6">Click "Load Defaults" to import existing projects, or "Add Project" to start fresh.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {(filterMissingDesc
              ? projects.filter(p => !p.description || !p.description.trim())
              : projects
            ).map((p) => {
              const missingDesc = !p.description || !p.description.trim();
              return (
              <div key={p.id} className={`bg-white border overflow-hidden group hover:shadow-md transition-shadow relative ${missingDesc ? 'border-[#C9A227]/60' : 'border-gray-200'}`} data-testid={`admin-project-${p.id}`}>
                {/* Missing description badge */}
                {missingDesc && (
                  <div
                    className="absolute top-2 left-2 z-10 flex items-center gap-1 bg-[#C9A227] text-black px-2 py-1 text-[9px] tracking-wider uppercase font-bold shadow-md"
                    title="This project has no description — click Edit to add one"
                    data-testid="missing-description-badge"
                  >
                    <AlertTriangle className="w-3 h-3" />
                    No description
                  </div>
                )}
                {/* Image/Color preview */}
                <div className="aspect-video relative" style={{ backgroundColor: p.color || '#1e3a5f' }}>
                  {p.image ? (
                    <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="text-white/80 text-3xl font-light tracking-wider">{getInitials(p.name)}</span>
                    </div>
                  )}
                  {/* Action overlay */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center gap-3 opacity-0 group-hover:opacity-100">
                    <button onClick={() => startEdit(p)} className="p-2 bg-white/90 hover:bg-[#C9A227] transition-colors" title="Edit">
                      <Edit3 className="w-4 h-4 text-black" />
                    </button>
                    <button onClick={() => handleDelete(p.id)} className="p-2 bg-white/90 hover:bg-red-500 hover:text-white transition-colors" title="Delete">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {/* Info */}
                <div className="p-3">
                  <span className="text-[9px] tracking-wider uppercase text-[#C9A227] font-medium">{p.category}</span>
                  <h3 className="text-sm font-medium text-black truncate">{p.name}</h3>
                  {missingDesc ? (
                    <p className="text-[10px] text-[#C9A227] italic mt-0.5">Description missing</p>
                  ) : (
                    <p className="text-[10px] text-gray-500 line-clamp-2 mt-0.5">{p.description}</p>
                  )}
                  {p.link && <p className="text-[10px] text-gray-400 truncate mt-1">{p.link}</p>}
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showAdd && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center px-4" onClick={resetForm}>
          <div className="bg-white w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="bg-[#0a0a0a] px-6 py-4 flex items-center justify-between">
              <span className="text-white text-xs tracking-wider uppercase">{editing ? 'Edit Project' : 'Add Project'}</span>
              <button onClick={resetForm} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
            </div>

            <div className="p-6 space-y-4">
              {/* Image preview */}
              <div className="relative">
                <div className={`aspect-video border border-dashed flex items-center justify-center overflow-hidden transition-colors ${uploading ? 'border-[#C9A227] bg-[#C9A227]/5 cursor-wait' : 'border-gray-300 cursor-pointer hover:border-[#C9A227]'}`} onClick={() => !uploading && fileRef.current?.click()}>
                  {uploading ? (
                    <div className="text-center">
                      <Loader2 className="w-7 h-7 text-[#C9A227] animate-spin mx-auto mb-2" />
                      <p className="text-[#C9A227] text-xs tracking-wider uppercase font-medium">Uploading...</p>
                    </div>
                  ) : form.image ? (
                    <img src={form.image} alt="Preview" className="w-full h-full object-cover" />
                  ) : (
                    <div className="text-center">
                      <Upload className="w-6 h-6 text-gray-300 mx-auto mb-2" />
                      <p className="text-gray-400 text-xs">Click to upload image</p>
                      <p className="text-gray-300 text-[10px] mt-1">JPG, PNG, WEBP &bull; Max 8 MB</p>
                    </div>
                  )}
                </div>
                {form.image && !uploading && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); fileRef.current?.click(); }}
                    className="absolute top-2 right-2 bg-black/70 hover:bg-black text-white text-[10px] tracking-wider uppercase px-3 py-1.5 transition-colors"
                  >
                    Replace
                  </button>
                )}
                {uploadError && (
                  <p className="text-red-500 text-xs mt-2" data-testid="upload-error">{uploadError}</p>
                )}
                <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" />
              </div>

              {/* Name */}
              <div>
                <label className="block text-[10px] font-medium text-gray-500 uppercase tracking-wider mb-1">Project Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))}
                  placeholder="e.g., Hanna Hospitality"
                  className="w-full px-4 py-3 border border-gray-200 text-sm focus:outline-none focus:border-[#C9A227]"
                  data-testid="project-name-input"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-[10px] font-medium text-gray-500 uppercase tracking-wider mb-1">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))}
                  placeholder="Brief description of the project..."
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-200 text-sm focus:outline-none focus:border-[#C9A227] resize-none"
                  data-testid="project-description-input"
                />
              </div>

              {/* Category */}
              <div>
                <label className="block text-[10px] font-medium text-gray-500 uppercase tracking-wider mb-1">Category</label>
                <select
                  value={form.category}
                  onChange={(e) => setForm(p => ({ ...p, category: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-200 text-sm focus:outline-none focus:border-[#C9A227]"
                >
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              {/* Link */}
              <div>
                <label className="block text-[10px] font-medium text-gray-500 uppercase tracking-wider mb-1">Website URL</label>
                <input
                  type="url"
                  value={form.link}
                  onChange={(e) => setForm(p => ({ ...p, link: e.target.value }))}
                  placeholder="https://example.com"
                  className="w-full px-4 py-3 border border-gray-200 text-sm focus:outline-none focus:border-[#C9A227]"
                />
              </div>

              {/* Color (fallback when no image) */}
              <div>
                <label className="block text-[10px] font-medium text-gray-500 uppercase tracking-wider mb-1">Card Color (used when no image)</label>
                <div className="flex gap-2 flex-wrap">
                  {COLORS.map(c => (
                    <button
                      key={c}
                      onClick={() => setForm(p => ({ ...p, color: c }))}
                      className={`w-8 h-8 rounded-sm border-2 transition-all ${form.color === c ? 'border-black scale-110' : 'border-transparent'}`}
                      style={{ backgroundColor: c }}
                    />
                  ))}
                </div>
              </div>

              {/* Save error */}
              {saveError && (
                <div className="bg-red-50 border border-red-200 p-3 flex items-start gap-2" data-testid="save-error">
                  <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-red-600 text-xs leading-relaxed">{saveError}</p>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <button onClick={resetForm} className="flex-1 py-3 border border-gray-200 text-gray-500 text-xs tracking-wider uppercase hover:bg-gray-50 transition-colors">
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving || !form.name.trim()}
                  className="flex-1 py-3 bg-[#0a0a0a] text-white text-xs tracking-wider uppercase font-medium hover:bg-[#C9A227] hover:text-black disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                  data-testid="save-project-btn"
                >
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                  {editing ? 'Update' : 'Add Project'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPortfolioPage;
